/**
 * ICS Calendar Export Utility
 * Generates .ics files for calendar import
 */

export interface ICSEvent {
  id: string
  title: string
  start_date: string  // YYYY-MM-DD
  start_time: string | null  // HH:MM or HH:MM:SS
  end_time: string | null
  location: string | null
  description?: string
}

/**
 * Format date/time to ICS format
 * If time is provided: YYYYMMDDTHHMMSS
 * If no time: YYYYMMDD (all-day event)
 */
function formatICSDateTime(date: string, time: string | null): string {
  const datePart = date.replace(/-/g, '')

  if (time) {
    const timePart = time.replace(/:/g, '').slice(0, 6).padEnd(6, '0')
    return `${datePart}T${timePart}`
  }

  return datePart
}

/**
 * Escape special characters in ICS text fields
 */
function escapeICSText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
}

/**
 * Generate a unique ID for an ICS event
 */
function generateUID(eventId: string): string {
  return `${eventId}@tsa-gardening.app`
}

/**
 * Generate ICS content for multiple events
 */
export function generateICS(events: ICSEvent[], calendarName: string = 'TSA Uppdrag'): string {
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//TSA Gardening//Calendar Export//SV',
    `X-WR-CALNAME:${escapeICSText(calendarName)}`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH'
  ]

  for (const event of events) {
    const uid = generateUID(event.id)
    const dtstart = formatICSDateTime(event.start_date, event.start_time)

    // Calculate end time - if no end_time, use start_time + 1 hour or all-day
    let dtend: string
    if (event.end_time) {
      dtend = formatICSDateTime(event.start_date, event.end_time)
    } else if (event.start_time) {
      // Add 1 hour to start time
      const [hours, minutes] = event.start_time.split(':').map(Number)
      const endHours = String(Math.min(hours + 1, 23)).padStart(2, '0')
      dtend = formatICSDateTime(event.start_date, `${endHours}:${String(minutes).padStart(2, '0')}`)
    } else {
      // All-day event - end date is next day
      const startDate = new Date(event.start_date)
      startDate.setDate(startDate.getDate() + 1)
      dtend = startDate.toISOString().slice(0, 10).replace(/-/g, '')
    }

    lines.push('BEGIN:VEVENT')
    lines.push(`UID:${uid}`)
    lines.push(`DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').slice(0, 15)}Z`)

    // Use VALUE=DATE for all-day events
    if (event.start_time) {
      lines.push(`DTSTART:${dtstart}`)
      lines.push(`DTEND:${dtend}`)
    } else {
      lines.push(`DTSTART;VALUE=DATE:${dtstart}`)
      lines.push(`DTEND;VALUE=DATE:${dtend}`)
    }

    lines.push(`SUMMARY:${escapeICSText(event.title)}`)

    if (event.location) {
      lines.push(`LOCATION:${escapeICSText(event.location)}`)
    }

    if (event.description) {
      lines.push(`DESCRIPTION:${escapeICSText(event.description)}`)
    }

    lines.push('END:VEVENT')
  }

  lines.push('END:VCALENDAR')

  return lines.join('\r\n')
}

/**
 * Download ICS file
 */
export function downloadICS(content: string, filename: string = 'kalender.ics'): void {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}
